import React from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsEvents } from '../utils/analytics';
import { scrollToTop } from '../hooks/useScrollToTop';

interface ResultDisplayEnhancedProps {
  frequency: number;
  iterations: number;
  onReset: () => void;
}

export const ResultDisplayEnhanced: React.FC<ResultDisplayEnhancedProps> = ({
  frequency,
  iterations,
  onReset,
}) => {
  const navigate = useNavigate();

  const getMusicalNote = (freq: number): string => {
    const A4 = 440;
    const C0 = A4 * Math.pow(2, -4.75);
    const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    
    if (freq === 0) return '';
    
    const halfStepsFromC0 = 12 * Math.log2(freq / C0);
    const octave = Math.floor(halfStepsFromC0 / 12);
    const noteIndex = Math.round(halfStepsFromC0 % 12);
    
    return `${noteNames[noteIndex]}${octave}`;
  };

  const handleStartTherapy = () => {
    analyticsEvents.trackFeatureInteraction('desktop_result', 'start_therapy', frequency);
    navigate(`/notched-white-noise?freq=${frequency}`);
    setTimeout(() => scrollToTop('smooth'), 100);
  };

  const handleSaveResults = () => {
    const resultSummary = `Tinnitus Frequency Assessment Results

Frequency: ${frequency} Hz
Musical Note: ${getMusicalNote(frequency)}
Test Date: ${new Date().toLocaleDateString()}
Questions Asked: ${iterations}
Accuracy: ±5 Hz
Test Method: Binary search algorithm

Your personalized sound therapy is available at:
${window.location.origin}/notched-white-noise?freq=${frequency}
`;
    
    const blob = new Blob([resultSummary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tinnitus-assessment-${frequency}hz-${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    
    analyticsEvents.trackFeatureInteraction('desktop_result', 'save_results', frequency);
  };

  const handleLearnMore = () => {
    analyticsEvents.trackFeatureInteraction('desktop_result', 'learn_more', frequency);
    navigate('/how-it-works');
    setTimeout(() => scrollToTop('smooth'), 100);
  };

  const handleRestartTest = () => {
    analyticsEvents.trackFeatureInteraction('desktop_result', 'restart_test', frequency);
    onReset();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl p-8 shadow-lg text-center">
        {/* Success State */}
        <div className="mb-8">
          <div className="text-4xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-purple-700 mb-2">Assessment Complete!</h1>
          <div className="text-5xl font-bold text-purple-800 mb-2">{frequency} Hz</div>
          <div className="text-sm text-gray-500 mb-3">Musical equivalent: {getMusicalNote(frequency)}</div>
          <p className="text-gray-600">Your personalized sound therapy is ready</p>
        </div>
        
        {/* Primary Action */}
        <button
          onClick={handleStartTherapy}
          className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-lg text-lg mb-6 transition-all duration-200"
        >
          Try Your Sound Therapy →
        </button>
        
        {/* Secondary Actions */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <button 
            onClick={handleSaveResults}
            className="py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            title="Download your results for medical records"
          >
            Save Result
          </button>
          <button 
            onClick={handleLearnMore}
            className="py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            title="Learn more about sound therapy"
          >
            Learn More
          </button>
          <button 
            onClick={handleRestartTest}
            className="py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            title="Take the assessment again"
          >
            Test Again
          </button>
        </div>
      </div>
      
      {/* Optional: Subtle info card below */}
      <div className="mt-6 text-center">
        <div className="bg-purple-50 rounded-lg p-4 inline-block">
          <p className="text-sm text-purple-800">
            <strong>Test Summary:</strong> {iterations} questions • ±5 Hz accuracy • {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};